import { z } from 'zod';
import { REMARK_KINDS, REMARK_SOURCES, type RemarkKind, type RemarkSource } from './supervision';

/* ─────────── Обсуждения у записи (раздел 6.20: УВ-01) ─────────── */

/** Виды карточек с обсуждением в первом срезе */
export const DISCUSSION_ENTITY_TYPES = {
  purchase_request: 'Заявка на закупку',
  psd_remark: 'Замечание',
} as const;
export type DiscussionEntityType = keyof typeof DISCUSSION_ENTITY_TYPES;
export const DiscussionEntityType = z.enum(
  Object.keys(DISCUSSION_ENTITY_TYPES) as [DiscussionEntityType, ...DiscussionEntityType[]],
);

export const DiscussionMessageInput = z.object({
  entityType: DiscussionEntityType,
  entityId: z.uuid(),
  body: z.string().trim().min(1).max(4000),
});
export type DiscussionMessageInput = z.infer<typeof DiscussionMessageInput>;

/** Что можно создать из сообщения в срезе УВ-02 (согласование и изменение — позже) */
export const DISCUSSION_SPAWN_KINDS = {
  task: 'Поручение',
  psd_question: 'Вопрос к проекту',
  psd_remark: 'Замечание надзора',
} as const;
export type DiscussionSpawnKind = keyof typeof DISCUSSION_SPAWN_KINDS;
export const DiscussionSpawnKind = z.enum(
  Object.keys(DISCUSSION_SPAWN_KINDS) as [DiscussionSpawnKind, ...DiscussionSpawnKind[]],
);

export const DiscussionCreatedLink = z.object({
  kind: DiscussionSpawnKind,
  id: z.uuid(),
  title: z.string(),
  path: z.string(),
});
export type DiscussionCreatedLink = z.infer<typeof DiscussionCreatedLink>;

export const DiscussionMessage = z.object({
  id: z.uuid(),
  entityType: DiscussionEntityType,
  entityId: z.uuid(),
  authorId: z.uuid(),
  authorName: z.string(),
  body: z.string(),
  createdAt: z.string(),
  /** Сообщение написал текущий пользователь */
  mine: z.boolean(),
  /** Записи, созданные из этого сообщения (УВ-02) */
  created: z.array(DiscussionCreatedLink),
});
export type DiscussionMessage = z.infer<typeof DiscussionMessage>;

/** Лента «мои недавние» для вкладки «Сообщения» (УВ-12 ← УВ-01) */
export const DiscussionThreadPreview = z.object({
  entityType: DiscussionEntityType,
  entityId: z.uuid(),
  /** Короткий заголовок карточки: номер и суть */
  title: z.string(),
  /** Путь в портале к карточке */
  path: z.string(),
  lastBody: z.string(),
  lastAuthorName: z.string(),
  lastAt: z.string(),
  messageCount: z.number().int().positive(),
});
export type DiscussionThreadPreview = z.infer<typeof DiscussionThreadPreview>;

const DateStr = z.iso.date();

/** Создать поручение, вопрос или замечание из сообщения обсуждения (УВ-02) */
export const DiscussionSpawnInput = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('task'),
    title: z.string().trim().min(3).max(300).optional(),
    assigneeId: z.uuid(),
    dueDate: DateStr.nullable().optional(),
  }),
  z.object({
    kind: z.literal('psd_question'),
    subject: z.string().trim().min(3).max(300).optional(),
    body: z.string().trim().min(3).max(10000).optional(),
  }),
  z.object({
    kind: z.literal('psd_remark'),
    title: z.string().trim().min(3).max(300).optional(),
    description: z.string().trim().max(5000).nullable().optional(),
    source: z.enum(Object.keys(REMARK_SOURCES) as [RemarkSource, ...RemarkSource[]]),
    remarkKind: z.enum(Object.keys(REMARK_KINDS) as [RemarkKind, ...RemarkKind[]]),
    dueDate: DateStr,
    assigneeId: z.uuid().nullable().optional(),
    stopWork: z.boolean().optional(),
  }),
]);
export type DiscussionSpawnInput = z.infer<typeof DiscussionSpawnInput>;

export const DiscussionSpawnResult = z.object({
  created: DiscussionCreatedLink,
  message: DiscussionMessage,
});
export type DiscussionSpawnResult = z.infer<typeof DiscussionSpawnResult>;

/** Обратная ссылка: из поручения/вопроса на сообщение обсуждения (УВ-02) */
export const DiscussionOrigin = z.object({
  messageId: z.uuid(),
  entityType: DiscussionEntityType,
  entityId: z.uuid(),
  title: z.string(),
  path: z.string(),
  bodyPreview: z.string(),
});
export type DiscussionOrigin = z.infer<typeof DiscussionOrigin>;
